import React, { useCallback, useMemo } from 'react';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WriteBackToFountainAuthMethod } from 'libs/end-to-end/src/types/databases/workflows/strategies/fountain-write-back';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';
import { useConnectionsConfiguration } from 'apps/sigma2-ui/src/lib/hooks/useConnectionsConfiguration';

const FountainConnectionSelector = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const connections = useConnectionsConfiguration(organizationId);
  const isAvailable = useMemo(() => {
    if (connections.state !== 'succeeded' || !connections.config?.isEnabled) {
      return false;
    }
    const fountain = connections.config.connections?.fountain;
    return (
      fountain?.isEnabled === true &&
      fountain?.installation?.isInstalled === true
    );
  }, [connections]);

  return (
    <div className="space-y-4">
      {connections.state === 'loading' && (
        <p className="text-gray-600">Loading connections...</p>
      )}
      {connections.state === 'succeeded' && isAvailable && (
        <p className="text-sm text-gray-600">
          Using the organization&apos;s Fountain connection.
        </p>
      )}
      {connections.state === 'succeeded' && !isAvailable && (
        <p className="text-gray-600">
          No Fountain connection is configured for this organization. Configure
          it in Organization Settings → Connections. OAuth installs also require
          hire API base URL in organization settings.
        </p>
      )}
      {connections.state === 'failed' && (
        <p className="text-red-600">
          {connections.errorMessage ?? 'Failed to load connections.'}
        </p>
      )}
    </div>
  );
};

export interface ConfigureWriteBackFountainAuthMethodProps
  extends ConfigureWorkflowBaseProps {
  authMethod: WriteBackToFountainAuthMethod | null;

  updateAuthMethod: (authMethod: WriteBackToFountainAuthMethod | null) => void;
}

export const ConfigureWriteBackFountainAuthMethod = ({
  authMethod,
  updateAuthMethod,
  listDataSetsPattern,
  organizationId,
}: ConfigureWriteBackFountainAuthMethodProps) => {
  const setSelectedMethod = useCallback(
    (selected: 'none' | WriteBackToFountainAuthMethod['type']) => {
      if (selected === 'none') {
        updateAuthMethod(null);
      } else if (selected !== authMethod?.type) {
        if (selected === 'connection') {
          updateAuthMethod({ type: 'connection' });
        } else {
          updateAuthMethod({
            type: selected,
          });
        }
      }
    },
    [authMethod, updateAuthMethod],
  );

  const selectedMethod = useMemo(() => {
    return authMethod?.type ?? 'none';
  }, [authMethod]);

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">Configure Fountain Auth Method</h2>
      <p className="text-gray-600">
        Select and configure the authentication method for Fountain.
      </p>
      <SelectBoxWithSearch
        type="single"
        list={[
          { optionLabel: 'None', optionValue: 'none' },
          { optionLabel: 'Merge', optionValue: 'merge' },
          { optionLabel: 'Connection', optionValue: 'connection' },
        ]}
        label="Select an auth method"
        selected={selectedMethod}
        setSelected={setSelectedMethod}
      />

      {authMethod?.type === 'merge' && (
        <WorkflowDataSetSelector
          dataSetId={authMethod.dataSetId ?? null}
          title="Select a merge ATS data set"
          listDataSetsPattern={listDataSetsPattern}
          setDataSetId={(dataSetId) => {
            updateAuthMethod({
              ...authMethod,
              dataSetId: dataSetId ?? undefined,
            });
          }}
          filterFn={isMergeAtsIntegrationDataSet}
        />
      )}

      {authMethod?.type === 'connection' && (
        <FountainConnectionSelector organizationId={organizationId} />
      )}
    </div>
  );
};
